/* eslint-disable @typescript-eslint/no-namespace */
import styled from '@emotion/styled';
import { typography, sizes } from '@landsbankinn/ui';
import * as PrismicHelpers from '@prismicio/helpers';
import { PrismicRichText } from '@prismicio/react';
import cloneDeep from 'lodash.clonedeep';
import Link from 'next/link';
import React, { useContext } from 'react';
import { htmlSerializer, htmlSerializerHyphenated } from './htmlSerializer';
import { PrismicContext } from './prismicContext';

export interface Doc {
  id: string;
  uid: string;
  tags: string[];
  type: string;
  lang: string;
  isBroken: boolean;
}

export interface UrlResolverResult {
  href: string;
  as?: string;
  onClick?: any;
}

export type UrlResolverType = (
  doc?: Partial<Doc> | undefined,
  raw?: any,
) => UrlResolverResult;

export type LinkResolverType = (
  doc: Partial<Doc>,
  extra?: any,
) => string | undefined;

interface PrismicTextProps {
  value?: Prismic.RichText;
  text?: boolean;
  as?:
    | keyof JSX.IntrinsicElements
    | React.ComponentType<React.ComponentProps<any>>;
  isArticle?: boolean;
  hyphenate?: boolean;
}
interface PrismicUrlProps {
  value?: Partial<Prismic.Link>;
  query?: string;
  title?: string | Prismic.RichText;
  children?: React.ReactElement;
  as?:
    | keyof JSX.IntrinsicElements
    | React.ComponentType<React.ComponentProps<any>>;
}

const asText = (text: any) => {
  return [].concat(text).map((t: any) => ({ ...t, type: 'label' }));
};

export const WrapText = styled.div<{ isArticle?: boolean }>`
  ${({ isArticle }) =>
    isArticle ? typography.bodyEditorial : typography.body};

  /* pre-s from the html -> markdown -> prismic conversion seem to be quotes */
  pre {
    white-space: inherit;
    font-family: monospace;
  }
  p,
  ul,
  ol {
    margin-top: calc(${30 / 17} * 1em);
    margin-bottom: calc(${30 / 17} * 1em);
    list-style-position: inside;
    &:first-of-type {
      margin-top: 0;
    }
    &:last-child {
      margin-bottom: 0;
    }
    &:empty {
      display: none;
    }
  }
  ul {
    list-style-type: none;
  }
  ul li {
    ${typography.listStyle}
  }
  ol {
    list-style-type: decimal;
    list-style: none;
    counter-reset: custom-counter;
  }

  ol li {
    counter-increment: custom-counter;
    ${typography.olListStyle}
  }
  h1 {
    ${typography.h2}
    margin-bottom: calc((25 / ${sizes.h2Size}) * 1em);
    max-width: calc((490 / ${sizes.h2Size}) * 1em);
    font-style: normal;
    + p {
      margin-top: 0;
    }
  }

  h2 {
    ${typography.h3}
    margin-bottom: calc((25 / ${sizes.h3Size}) * 1em);
    max-width: calc((490 / ${sizes.h3Size}) * 1em);
    font-style: normal;
    + p {
      margin-top: 0;
    }
  }

  h3 {
    ${typography.h4}
    margin-bottom: calc((25 / ${sizes.h4Size}) * 1em);
    max-width: calc((490 / ${sizes.h4Size}) * 1em);
    font-style: normal;
    + p {
      margin-top: 0;
    }
  }

  h4,
  h5 {
    ${typography.h5}
    margin-bottom: calc((25 / ${sizes.h5Size}) * 1em);
    max-width: calc((490 / ${sizes.h5Size}) * 1em);
    font-style: normal;
    + p {
      margin-top: 0;
    }
  }

  small {
    ${typography.bodySmall}
  }

  em {
    font-style: italic;
  }

  strong,
  b {
    font-weight: 700;
  }
  a {
    text-decoration: underline;
    @media (pointer: fine) {
      &:hover {
        text-decoration: none;
      }
    }
  }

  > div:last-child {
    margin-bottom: 0;
  }
`;

export function PrismicText({
  value,
  as,
  isArticle,
  hyphenate,
}: // linkResolver,
PrismicTextProps) {
  const { linkResolver } = useContext(PrismicContext);
  if (!value) {
    return null;
  }

  const WrapTextAs = as ? WrapText.withComponent(as as any) : WrapText;
  return (
    <WrapTextAs isArticle={isArticle}>
      <PrismicRichText
        field={value as any}
        components={hyphenate ? htmlSerializerHyphenated : htmlSerializer}
        linkResolver={linkResolver as any}
      />
    </WrapTextAs>
  );
}

export function PrismicLink({
  value,
  title,
  query,
  children,
  ...rest
}: PrismicUrlProps) {
  const { urlResolver } = useContext(PrismicContext);
  const url = Prismic.link(urlResolver, value, title);

  if (!children) {
    return null;
  }

  // eslint-disable-next-line regexp/no-unused-capturing-group
  const isExternal = /^((https?:)?\/\/|[\da-zA-Z]+:)/.test(url.href || '');

  if (isExternal) {
    return React.cloneElement(children, {
      target: '_blank',
      rel: 'noopener norefferrer',
      href: url.href,
      ...children.props,
      ...rest,
    });
  }

  const anchorProps = url.anchorProps();
  if (query) {
    anchorProps.href = `${anchorProps.href}?${query}`;
  }

  return (
    <Link href={{ pathname: url.as, query }}>
      {children && children.props
        ? React.cloneElement(children, {
            ...anchorProps,
            ...children.props,
            ...rest,
          })
        : children}
    </Link>
  );
}

export enum LinkType {
  Document = 'Link.document',
  Image = 'Link.image',
  File = 'Link.file',
  Web = 'Link.web',
}
interface ValueDocument {
  _linkType: LinkType.Document;
  page_title?: Prismic.RichText;
  title?: Prismic.RichText;
  abstract?: Prismic.RichText;
  abstract_image?: Prismic.Image;
  image?: Prismic.Image & {
    ar_4_3?: Prismic.Image;
    ar_1_1?: Prismic.Image;
  };
  _meta: {
    id: string;
    uid?: string;
    type: string;
    lang: string;
  };
}
interface ValueImage {
  _linkType: LinkType.Image;
  name: string;
  url: string;
  size: number;
  height: number;
  width: number;
}
interface ValueFile {
  _linkType: LinkType.File;
  name: string;
  url: string;
  size: number;
}
interface ValueWeb {
  _linkType: LinkType.Web;
  linkType: LinkType.Web;
  url: string;
}

interface ValueRichTextLink {
  link_type: string;
  id: string;
  uid?: string;
  type: string;
  lang: string;
}

export namespace Prismic {
  export enum Elements {
    heading1 = 'heading1',
    heading2 = 'heading2',
    heading3 = 'heading3',
    heading4 = 'heading4',
    heading5 = 'heading5',
    heading6 = 'heading6',
    paragraph = 'paragraph',
    preformatted = 'preformatted',
    strong = 'strong',
    em = 'em',
    listItem = 'list-item',
    oListItem = 'o-list-item',
    list = 'group-list-item',
    oList = 'group-o-list-item',
    image = 'image',
    embed = 'embed',
    hyperlink = 'hyperlink',
    label = 'label',
    span = 'span',
  }
  interface RichTextSpan {
    start: number;
    end: number;
    type: 'strong' | 'hyperlink';
    data?: {
      link_type: string;
      url: string;
      target?: string;
    };
  }
  export interface RichText {
    type: Elements;
    text: string;
    spans: RichTextSpan[];
  }
  export type Link = ValueDocument | ValueImage | ValueFile | ValueWeb;
  export interface Image {
    alt?: string;
    url: string;
    copyright?: string;
    dimensions: {
      width: number;
      height: number;
    };
    seo?: Prismic.Image;
    ar_1_1?: Prismic.Image;
    ar_4_3?: Prismic.Image;
    ar_16_9?: Prismic.Image;
    aside?: Prismic.Image;
  }
  export interface PageInfo {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startCursor: string;
    endCursor: string;
  }
  export interface Embed {
    html: string;
    thumbnail_url: string;
    duration: number;
    title: string;
    provider_name: string;
    video_id: number;
    key: string;
    embed: any;
    oembed?: any;
  }

  export interface NextLink {
    href: string;
    as: string;
    title?: string;
    anchorProps?: () => {
      href?: string;
      title?: string;
      onClick?: any;
    };
  }

  export function text(value?: any): string | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      return value;
    }

    if (Array.isArray(value)) {
      const filtered = value.filter((item) => !!item);
      if (filtered.length === 0) {
        return null;
      }
      return PrismicHelpers.asText(filtered as any);
    }

    return PrismicHelpers.asText(value);
  }

  export function date(value: string) {
    if (!value) {
      return null;
    }

    return new Date(value);
  }

  export function link(
    urlResolver: (
      doc?: Partial<Doc> | undefined,
      raw?: any,
    ) => UrlResolverResult,
    value?: Partial<Prismic.Link>,
    overwriteTitle?: string | Prismic.RichText,
  ) {
    let href = '#unresolved';
    let title: string | null = 'Missing title';
    let as: string | undefined;
    let onClick: any;
    const anchorProps = () => ({ href: as, title, onClick });

    if (!value) {
      return {
        href,
        title,
        as,
        anchorProps,
      };
    }
    const result = cloneDeep(value);
    const possibleValue = value as ValueRichTextLink;
    switch (possibleValue.link_type) {
      case 'Media':
        result._linkType =
          (result as any).kind === 'document' ? LinkType.File : LinkType.Image;
        break;
      case 'Web':
        result._linkType = LinkType.Web;
        break;
      case 'Document':
        result._linkType = LinkType.Document;

        if (result._linkType === LinkType.Document) {
          result._meta = {
            id: possibleValue.id,
            lang: possibleValue.lang,
            uid: possibleValue.uid,
            type: possibleValue.type,
          };
        }
        break;
    }

    if (result._linkType === LinkType.Web && result.url) {
      href = result.url;
    }

    if (
      (result._linkType === LinkType.File ||
        result._linkType === LinkType.Image) &&
      result.url &&
      result.name
    ) {
      href = result.url;
      title = result.name;
    }

    if (result._linkType === LinkType.Document) {
      const url = urlResolver(result?._meta, result);
      href = url.href;
      as = url.as;
      onClick = url.onClick;

      title = result.page_title
        ? Prismic.text(result.page_title)
        : result.title
        ? Prismic.text(result.title)
        : title;
    }

    if (overwriteTitle) {
      title = Prismic.text(overwriteTitle);
    }

    return {
      href,
      title,
      as,
      onClick,
      anchorProps,
    };
  }
}
